import React from 'react';

/**
 * LocalPaginator
 * - Stateless, controlled paginator for pre-fetched datasets
 * - Props:
 *   - total: number (required) — total item count
 *   - perPage: number (default 10)
 *   - page: number (1-based current page)
 *   - onChange: (nextPage: number) => void
 */
export default function LocalPaginator({ total = 0, perPage = 10, page = 1, onChange = () => {} }) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / (Number(perPage) || 1)));
  const curr = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const go = (p) => {
    const next = Math.min(Math.max(1, Number(p) || 1), totalPages);
    if (next !== curr) onChange(next);
  };

  // Build a compact page list like: 1 2 3 4 5 ... N (when many pages)
  const pagesToShow = () => {
    const maxNumbers = 5; // show first 5, then ellipsis, then last
    const arr = [];
    if (totalPages <= maxNumbers) {
      for (let i = 1; i <= totalPages; i++) arr.push(i);
      return { list: arr, showEllipsis: false };
    }
    for (let i = 1; i <= maxNumbers; i++) arr.push(i);
    return { list: arr, showEllipsis: true };
  };

  const { list, showEllipsis } = pagesToShow();

  return (
    <div className="flex items-center justify-center gap-2 font-[Pretendard] text-[15px]">
      {/* Prev */}
      <button
        type="button"
        className="shrink-0 p-2 rounded-md cursor-pointer"
        aria-label="prev"
        onClick={() => go(curr - 1)}
        disabled={curr <= 1}
      >
        <img src={`${rmVars.themeUrl}images/pager_prev.png`} alt="prev" className="w-4 h-4" />
      </button>

      {/* Numbers */}
      {list.map((p) => {
        const isActive = p === curr;
        return (
          <button
            key={p}
            type="button"
            className={`px-[15px] py-2 rounded-md font-medium cursor-pointer ${
              isActive ? 'text-[#215BD8] bg-[#D7E2F9]' : 'text-[#6C7687] hover:bg-[#E4E8F1]'
            }`}
            onClick={() => go(p)}
          >
            {p}
          </button>
        );
      })}

      {showEllipsis && <button className="text-[#6C7687] select-none px-1" disabled>...</button>}

      {showEllipsis && (
        <button
          type="button"
          className="px-[15px] py-2 rounded-md text-[#6C7687] text-[15px] hover:bg-[#E4E8F1] font-medium cursor-pointer"
          onClick={() => go(totalPages)}
        >
          {totalPages}
        </button>
      )}

      {/* Next */}
      <button
        type="button"
        className="shrink-0 p-2 rounded-md cursor-pointer"
        aria-label="next"
        onClick={() => go(curr + 1)}
        disabled={curr >= totalPages}
      >
        <img src={`${rmVars.themeUrl}images/pager_next.png`} alt="next" className="w-4 h-4" />
      </button>
    </div>
  );
}
